import React, { useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Collapse,
  Divider,
  Grid,
  Card,
  CardContent,
  IconButton,
  useTheme,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Person,
  School,
  CardMembership,
  Stars,
  Notifications,
  Settings,
  ExpandLess,
  ExpandMore,
  MenuOpen,
  Group,
  TrendingUp,
  Assignment,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AdminNav from "../Pages/AdminNav";
import Footer from "../Components/Footer";

const drawerWidth = 280;

const AdminDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [openCourses, setOpenCourses] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const menuItems = [
    {
      text: "Dashboard",
      icon: <DashboardIcon />,
      path: "/db-shiksha/kaas/admin/dashboard",
    },
    {
      text: "Student View",
      icon: <Person />,
      path: "/db-shiksha/kaas/student/profile",
    },
    {
      text: "Courses",
      icon: <School />,
      subItems: [
        { text: "Add Course", path: "/db-shiksha/kaas/admin/addCourses" },
        { text: "Manage Tracks", path: "/db-shiksha/kaas/admin/adminNav" },
      ],
    },
    {
      text: "My Certificates",
      icon: <CardMembership />,
      path: "/db-shiksha/kaas/student/certificates",
    },
    {
      text: "My Achievements",
      icon: <Stars />,
      path: "/db-shiksha/kaas/student/achievements",
    },
    {
      text: "Notifications",
      icon: <Notifications />,
      path: "/db-shiksha/kaas/admin/notifications",
    },
    {
      text: "Settings",
      icon: <Settings />,
      path: "/db-shiksha/kaas/admin/settings",
    },
  ];

  const statCards = [
    {
      title: "Total Students",
      value: "1,250",
      icon: <Group sx={{ fontSize: 40, color: "#D4AF37" }} />,
      color: "#4caf50",
    },
    {
      title: "Active Courses",
      value: "48",
      icon: <Assignment sx={{ fontSize: 40, color: "#D4AF37" }} />,
      color: "#2196f3",
    },
    {
      title: "Total Revenue",
      value: "₹4.5L",
      icon: <TrendingUp sx={{ fontSize: 40, color: "#D4AF37" }} />,
      color: "#ff9800",
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AdminNav />
      <Box sx={{ display: "flex", flexGrow: 1, mt: "64px" }}>
        {/* Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            width: sidebarOpen ? drawerWidth : 80,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: sidebarOpen ? drawerWidth : 80,
              boxSizing: "border-box",
              mt: "64px",
              height: "calc(100vh - 64px)",
              bgcolor: "background.paper",
              borderRight: "1px solid",
              borderColor: "divider",
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: "hidden",
            },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
            <IconButton onClick={() => setSidebarOpen(!sidebarOpen)}>
              <MenuOpen
                sx={{ transform: sidebarOpen ? "none" : "rotate(180deg)" }}
              />
            </IconButton>
          </Box>
          <Divider />
          <List sx={{ px: 2, pt: 2 }}>
            {menuItems.map((item) => (
              <React.Fragment key={item.text}>
                {item.subItems ? (
                  <>
                    <ListItemButton
                      onClick={() => setOpenCourses(!openCourses)}
                      sx={{
                        borderRadius: "12px",
                        mb: 1,
                        justifyContent: sidebarOpen ? "initial" : "center",
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: sidebarOpen ? 40 : 0,
                          mr: sidebarOpen ? 1 : "auto",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {sidebarOpen && (
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                      )}
                      {sidebarOpen &&
                        (openCourses ? <ExpandLess /> : <ExpandMore />)}
                    </ListItemButton>
                    <Collapse
                      in={openCourses && sidebarOpen}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List component="div" disablePadding sx={{ pl: 4 }}>
                        {item.subItems.map((sub) => (
                          <ListItemButton
                            key={sub.text}
                            onClick={() => navigate(sub.path)}
                            sx={{ borderRadius: "10px", mb: 0.5 }}
                          >
                            <ListItemText
                              primary={sub.text}
                              primaryTypographyProps={{ fontSize: "0.9rem" }}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </>
                ) : (
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    sx={{
                      borderRadius: "12px",
                      mb: 1,
                      justifyContent: sidebarOpen ? "initial" : "center",
                      bgcolor:
                        window.location.pathname === item.path
                          ? "rgba(212, 175, 55, 0.1)"
                          : "transparent",
                      color:
                        window.location.pathname === item.path
                          ? "primary.main"
                          : "inherit",
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: sidebarOpen ? 40 : 0,
                        mr: sidebarOpen ? 1 : "auto",
                        color:
                          window.location.pathname === item.path
                            ? "primary.main"
                            : "inherit",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {sidebarOpen && (
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    )}
                  </ListItemButton>
                )}
              </React.Fragment>
            ))}
          </List>
        </Drawer>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 4,
            width: `calc(100% - ${sidebarOpen ? drawerWidth : 80}px)`,
            bgcolor: "background.default",
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" fontWeight={800} gutterBottom>
              Admin Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Welcome back, Administrator. Here's what's happening today.
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ mb: 6 }}>
            {statCards.map((card, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card
                  sx={{
                    borderRadius: "20px",
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "#D4AF37",
                      transform: "translateY(-5px)",
                    },
                  }}
                >
                  <CardContent
                    sx={{ display: "flex", alignItems: "center", p: 3 }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "12px",
                        bgcolor: "rgba(212, 175, 55, 0.1)",
                        mr: 2,
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        {card.title}
                      </Typography>
                      <Typography variant="h4" fontWeight={800}>
                        {card.value}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Placeholder for Recent Activity Table */}
          <Card sx={{ borderRadius: "20px", p: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Recent Activities
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 8 }}
              >
                Activity logs will be displayed here soon.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default AdminDashboard;
